import { useEffect, useRef, useState, useCallback } from 'react';
import { WeatherData } from '../types/weather';
import { AutomationState, FanMode, checkAutomation } from '../utils/automation';

interface UseAutomationProps {
  enabled: boolean;
  mode: FanMode;
  weatherData: WeatherData | null;
  onFanCommand: (command: 'ON' | 'OFF', isAuto: boolean) => void;
}

export const useAutomation = ({
  enabled,
  mode,
  weatherData,
  onFanCommand,
}: UseAutomationProps) => {
  const [state, setState] = useState<AutomationState>({
    enabled: false,
    mode: 'hot',
    fanAutoState: false,
    conditionMetSince: null,
    activationTimer: null,
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  // Update state when props change
  useEffect(() => {
    // Clear existing timer if disabling or mode changes
    if (state.activationTimer && (!enabled || state.mode !== mode)) {
      clearTimeout(state.activationTimer);
    }

    setState((prev) => ({
      ...prev,
      enabled,
      mode,
      conditionMetSince: enabled && prev.enabled && mode === prev.mode ? prev.conditionMetSince : null,
      activationTimer: enabled && prev.enabled && mode === prev.mode ? prev.activationTimer : null,
    }));
  }, [enabled, mode]);

  // Check automation when weather data changes
  useEffect(() => {
    if (!enabled || !weatherData) {
      return;
    }

    const newState = checkAutomation(
      stateRef.current,
      {
        temperature: weatherData.temperature,
        humidity: weatherData.humidity,
        pressure: weatherData.pressure ?? null,
        light: weatherData.light ?? null,
        rain: weatherData.rain ?? null,
        windSpeed: weatherData.windSpeed ?? null,
      },
      onFanCommand
    );

    setState(newState);
  }, [enabled, weatherData, onFanCommand]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (stateRef.current.activationTimer) {
        clearTimeout(stateRef.current.activationTimer);
      }
    };
  }, []);

  const setFanAutoState = useCallback((fanState: boolean) => {
    setState((prev) => ({ ...prev, fanAutoState: fanState }));
  }, []);

  return { setFanAutoState };
};

